// =======================================================================================
// ✅ Trekkin.app — createOrGetDirectChat.js v1.2.0 (RULES-SAFE + EVENT CONTEXT)
// =======================================================================================
// ✔ Cria ou reutiliza chat direto User ↔ Guia
// ✔ chatId determinístico (sem duplicados)
// ✔ Writes separados (compatível com Firebase Rules)
// ✔ Regista inbox do user em /userDirectChats
// ✔ Inbound do guia só aparece após primeira mensagem (evita chats fantasma)
// ✔ Inclui contexto do evento (eventId / eventName)
// ✔ Regra de Ouro: nada removido, apenas estendido
// =======================================================================================

import { ref, get, set, update, serverTimestamp, push } from "firebase/database";
import { db } from "../firebaseConfig";

async function findActiveChatForPair({ userId, guideId }) {
  let inbox = {};
  try {
    const inboxSnap = await get(ref(db, `userDirectChats/${userId}`));
    inbox = inboxSnap.val() || {};
  } catch (err) {
    console.warn("⚠️ findActiveChatForPair: falha ao ler userDirectChats:", {
      userId,
      error: err?.message || String(err),
    });
    return null;
  }

  let best = null;
  for (const [candidateId, meta] of Object.entries(inbox)) {
    if (!candidateId) continue;
    if (meta?.guideId && String(meta.guideId) !== String(guideId)) continue;

    let chatSnap = null;
    try {
      chatSnap = await get(ref(db, `directChats/${candidateId}`));
    } catch (err) {
      // Alguns registos antigos/inválidos podem não ser legíveis pelo utilizador atual.
      // Não deve bloquear a criação/reutilização do chat correto.
      console.warn("⚠️ findActiveChatForPair: chat inacessível, ignorado:", {
        candidateId,
        error: err?.message || String(err),
      });
      continue;
    }
    if (!chatSnap?.exists()) continue;

    const chat = chatSnap.val() || {};
    if (String(chat.userId) !== String(userId)) continue;
    if (String(chat.guideId) !== String(guideId)) continue;
    if (chat.ended === true) continue;

    const score = Number(
      chat.lastMessageAt || chat.createdAt || meta?.lastMessageAt || meta?.ts || 0
    );
    if (!best || score > best.score) {
      best = { chatId: candidateId, score };
    }
  }

  return best?.chatId || null;
}

/**
 * Cria ou reutiliza um chat direto entre um user e um guia
 * @param {Object} params
 * @param {string} params.userId
 * @param {string} params.guideId
 * @param {string=} params.eventId
 * @param {string=} params.eventName
 * @returns {Promise<string>} chatId
 */
export async function createOrGetDirectChat({
  userId,
  guideId,
  eventId = null,
  eventName = null,
}) {
  if (!userId || !guideId) {
    throw new Error("createOrGetDirectChat: userId ou guideId em falta");
  }

  // --------------------------------------------------
  // chatId determinístico → evita duplicados
  // --------------------------------------------------
  const chatId =
    userId < guideId
      ? `${userId}_${guideId}`
      : `${guideId}_${userId}`;

  const chatRef = ref(db, `directChats/${chatId}`);
  const snap = await get(chatRef);
  const deterministic = snap.val() || null;

  let resolvedChatId = chatId;

  const hasPairFields = (chat) =>
    !!chat &&
    String(chat.userId || "") === String(userId) &&
    String(chat.guideId || "") === String(guideId);

  if (deterministic && deterministic.ended !== true) {
    // Chats antigos podem existir sem userId/guideId (ex: userUid/guideUid).
    // Sem estes campos, as rules de directMessages bloqueiam a leitura.
    if (!hasPairFields(deterministic)) {
      await update(chatRef, {
        chatId,
        userId,
        guideId,
        lastMessageAt:
          deterministic.lastMessageAt ||
          deterministic.ts ||
          deterministic.createdAt ||
          null,
      });
    }
    resolvedChatId = chatId;
  } else {
    const activeChatId = await findActiveChatForPair({ userId, guideId });
    if (activeChatId) {
      // Garantir consistência do chat recuperado para não falhar nas rules de mensagens.
      try {
        await update(ref(db, `directChats/${activeChatId}`), {
          chatId: activeChatId,
          userId,
          guideId,
        });
      } catch {}
      resolvedChatId = activeChatId;
    } else if (!deterministic) {
      await set(chatRef, {
        chatId,
        userId,
        guideId,
        createdAt: serverTimestamp(),
        lastMessage: "",
        lastMessageAt: null,
        unread: {
          user: false,
          guide: false,
        },
      });
      resolvedChatId = chatId;
    } else {
      const newChatRef = push(ref(db, "directChats"));
      const newChatId = newChatRef.key;

      if (!newChatId) {
        throw new Error("createOrGetDirectChat: falha ao gerar novo chatId");
      }

      await set(newChatRef, {
        chatId: newChatId,
        userId,
        guideId,
        createdAt: serverTimestamp(),
        lastMessage: "",
        lastMessageAt: null,
        unread: {
          user: false,
          guide: false,
        },
      });

      resolvedChatId = newChatId;
    }
  }

  // --------------------------------------------------
  // REGISTAR INBOX (WRITES SEPARADOS → RULES SAFE)
  // --------------------------------------------------

  // Inbox do user (apenas o próprio user escreve)
  await update(ref(db, `userDirectChats/${userId}/${resolvedChatId}`), {
    chatId: resolvedChatId,
    guideId,
    eventId,
    eventName,
    ts: serverTimestamp(),
    unread: false,
    // Ao reutilizar um chat ainda ativo, reabre a entrada que possa ter ficado
    // arquivada por uma sessão anterior.
    archived: null,
    ended: null,
    endedAt: null,
  });

  return resolvedChatId;
}
